import React from 'react';
import { X, Users, Phone, FileText } from 'lucide-react';
import { useAppSelector } from '../../store/hooks';
import Modal from '../UI/Modal';
import Button from '../UI/Button';

interface SkippedDuplicate {
  sheet: string;
  row: number;
  guest_name: string;
  phone: string;
  existing_guest_name: string;
  existing_guest_phone: string;
}

interface HistoricalEventImportSkippedDuplicatesProps {
  isOpen: boolean;
  onClose: () => void;
  skippedDuplicates: SkippedDuplicate[];
}

const HistoricalEventImportSkippedDuplicates: React.FC<HistoricalEventImportSkippedDuplicatesProps> = ({
  isOpen,
  onClose,
  skippedDuplicates
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Skipping Duplicates"
      size="lg"
    >
      <div className="space-y-4">
        {/* Header Message */}
        <div className={`border rounded-lg p-4 ${
          theme === 'dark' 
            ? 'bg-yellow-900/20 border-yellow-700' 
            : 'bg-yellow-50 border-yellow-200'
        }`}>
          <div className="flex items-center">
            <Users className={`h-5 w-5 mr-2 ${
              theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
            }`} />
            <h3 className={`text-sm font-medium ${
              theme === 'dark' ? 'text-yellow-300' : 'text-yellow-900'
            }`}>
              {skippedDuplicates.length} duplicate{skippedDuplicates.length !== 1 ? 's' : ''} skipped
            </h3>
          </div>
          <p className={`text-sm mt-1 ${
            theme === 'dark' ? 'text-yellow-200' : 'text-yellow-800'
          }`}>
            The following guests were skipped because they already exist in the system (matched by phone number).
          </p>
        </div>

        {/* Skipped Duplicates Table */}
        <div className={`border rounded-lg overflow-hidden ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className={`min-w-full divide-y ${
              theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'
            }`}>
              <thead className={`sticky top-0 ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'
              }`}>
                <tr>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    Sheet
                  </th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    Row
                  </th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    Imported Name
                  </th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    Imported Phone
                  </th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    Existing Guest
                  </th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    Existing Phone
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${
                theme === 'dark' 
                  ? 'bg-gray-900 divide-gray-700' 
                  : 'bg-white divide-gray-200'
              }`}>
                {skippedDuplicates.map((duplicate, index) => (
                  <tr key={index} className={theme === 'dark' ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      <div className="flex items-center">
                        <FileText className={`h-4 w-4 mr-2 ${
                          theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                        }`} />
                        {duplicate.sheet}
                      </div>
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      {duplicate.row}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      {duplicate.guest_name || '-'}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      <div className="flex items-center">
                        <Phone className={`h-3 w-3 mr-1 ${
                          theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                        }`} />
                        {duplicate.phone || '-'}
                      </div>
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      {duplicate.existing_guest_name || '-'}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      <div className="flex items-center">
                        <Phone className={`h-3 w-3 mr-1 ${
                          theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                        }`} />
                        {duplicate.existing_guest_phone || '-'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default HistoricalEventImportSkippedDuplicates;

